import { DashboardEventDetails } from "v2/components/dashboard/DashboardEventDetails/DashboardEventDetails";
import TeamSchedule from "v2/components/TeamSchedule";
import { AgendaWeek } from "@webV2/components/dashboard/AgendaWeek";
import { cn } from "@v2/lib/utils";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent
} from "@v2/components/ui/sidebar";
import { WeekSwitch } from "@cmp-src/components/calendar/WeekSwitch";
import { OOORequest } from "@/components/attendance/employee/OOORequest";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";
import { Tabs, TabsContent } from "@v2/components/ui/tabs";
import { Visitors } from "@webV2/components/dashboard-subpages/Visitors";
import { News } from "@webV2/components/dashboard-subpages/News";
import { SiteInfo } from "@webV2/components/dashboard-subpages/SiteInfo";
import { DashboardContentWrapper } from "@cmp-src/components/dashboard/DashboardContentWrapper";
import { SelectOffices } from "@webV2/components/SelectOffices";
import { ColumnWidthProvider } from "@gettactic/components/src/contexts/ColumnWidthContext";
import {
  CardV2LayoutClasses,
  MobileBottomCalcVh
} from "@v2/components/display/Card/classes";
import { Switch } from "@v2/components/ui/Switch";
import { CalendarRange } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { CloseIcon } from "@v2/icons";
import { useDashboardData } from "@webV2/dashboard/useDashboardData";
import { DashboardTabsList } from "@webV2/dashboard/partial/DashboardTabsList";
import { useHasStations, useSettings } from "@gettactic/hooks";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { useMemo } from "react";
import { PageOptions } from "@/contexts/DashboardStateContext";
import { pageOptionToTab } from "@webV2/dashboard/defines";
import { getDefaultDashboardTab } from "@gettactic/helpers";

export default function V2Dashboard() {
  const { hasStations } = useHasStations();
  const { isBreakpoint } = useBreakpoint();

  const { isOrgPrivacyEnabled } = useSettings();

  const defaultTab: PageOptions = useMemo(
    () => getDefaultDashboardTab(isOrgPrivacyEnabled, false),
    [isOrgPrivacyEnabled]
  );

  const isMd = isBreakpoint("md");

  const {
    shouldRenderContent,
    selectedEvent,
    setSelectedEvent,
    containerRef,
    agendaDisclosure,
    isAdmin,
    hasPages,
    weekDate,
    setWeekDate,
    isComponentLoading,
    nextWeek,
    prevWeek,
    scheduleMeData,
    oooRequest,
    setOooRequest,
    dashboardFilters,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback,
    setOffices,
    dashboardWidth,
    handleTabChange,
    activeTab,
    WHOLE_WIDTH,
    locationData,
    finalDaysCount,
    setPage
  } = useDashboardData({ defaultTab });

  if (!shouldRenderContent) {
    return null; // Return null until router is ready to prevent hooks from being conditionally rendered
  }

  const calendarSwitch = (
    <button
      className={"flex items-center gap-1 md:hidden"}
      onClick={() => {
        agendaDisclosure?.toggle();
      }}
    >
      <CalendarRange className={"size-4"} />
      <Switch
        checked={agendaDisclosure?.isOpen || false}
        variant={"squared"}
        size={"xs"}
      />
    </button>
  );

  // Always render both components, but hide one with CSS
  return (
    <>
      <div className={"relative h-full"}>
        <div className={cn("flex h-full flex-row")}>
          <div
            data-state={agendaDisclosure?.isOpen ? "open" : "closed"}
            className={cn(
              agendaDisclosure?.isOpen ? "fixed" : "hidden",
              "top-12 right-4 bottom-0 left-0 z-sticky flex-col bg-white md:static md:flex md:bg-transparent",
              !isMd &&
                "data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in",
              !isMd &&
                "data-[state=closed]:zoom-out-90 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out"
            )}
            style={isMd ? { width: WHOLE_WIDTH } : undefined}
          >
            <div className="flex w-full items-center justify-between gap-2 pt-v2-layout-card-out pl-v2-layout-card-out text-sm">
              {calendarSwitch}
              <WeekSwitch
                prevWeek={prevWeek}
                nextWeek={nextWeek}
                setWeekDate={setWeekDate}
                weekDate={`${weekDate}T00:00:00.000Z`}
              />
            </div>
            <ScrollArea.Root
              type="hover"
              className={cn(
                "pt-6 pl-v2-layout-card-out md:h-[calc(100vh-4rem)] "
              )}
            >
              <ScrollArea.Viewport
                className={`${MobileBottomCalcVh} w-full md:h-full`}
              >
                <SidebarContent className=" ">
                  <SidebarGroup className="px-0">
                    <SidebarGroupContent className="">
                      {scheduleMeData.data ? (
                        <AgendaWeek locationData={locationData} />
                      ) : null}
                    </SidebarGroupContent>
                  </SidebarGroup>
                </SidebarContent>
              </ScrollArea.Viewport>
              <Scrollbar className="-mr-v2-layout-card-scrollbar" />
            </ScrollArea.Root>
          </div>
          <div
            className={cn(
              "relative flex flex-1 flex-col overflow-hidden",
              `${CardV2LayoutClasses} w-full transition-[width] duration-300 ease-in-out`,
              "ml-0 md:m-v2-layout-card-out"
            )}
          >
            <ColumnWidthProvider
              daysCount={finalDaysCount}
              containerRef={containerRef}
            >
              <Tabs
                defaultValue={pageOptionToTab[defaultTab]}
                value={activeTab}
                onValueChange={(value) => {
                  handleTabChange(value);
                }}
                className="w-full items-center justify-center"
              >
                <div className="flex w-full flex-col items-center justify-center">
                  <DashboardContentWrapper maxWidth={dashboardWidth}>
                    <div
                      className="flex w-full flex-row items-center justify-between gap-6 md:gap-0"
                      ref={containerRef}
                    >
                      {calendarSwitch ? (
                        <div className={"mt-7 mb-4 "}>{calendarSwitch}</div>
                      ) : null}

                      <DashboardTabsList
                        hasPages={hasPages}
                        isAdmin={isAdmin}
                        selectedEvent={selectedEvent}
                        setSelectedEvent={setSelectedEvent}
                        showAttendanceTab={!isOrgPrivacyEnabled}
                        setPage={setPage}
                        defaultPage={defaultTab}
                      />
                      {!selectedEvent && (
                        <div className={"mt-3 md:mt-0"}>
                          <SelectOffices
                            // offices={dashboardFilters.offices ?? undefined}
                            onChange={(ids: string[] | string) => {
                              setOffices(Array.isArray(ids) ? ids[0] : ids);
                            }}
                            defaultValue={dashboardFilters.office ?? undefined}
                            disabled={isComponentLoading}
                            showSelectedLabel={isMd}
                            allowClear={false}
                          />
                        </div>
                      )}
                    </div>
                  </DashboardContentWrapper>
                </div>

                <ScrollArea.Root
                  type="hover"
                  scrollHideDelay={500}
                  className={`flex-1 ${MobileBottomCalcVh} md:h-[calc(100vh-10rem)]`}
                >
                  <ScrollArea.Viewport className="[&>div]:!block block h-full w-full">
                    <div className="flex w-full flex-col items-center justify-center">
                      <DashboardContentWrapper maxWidth={dashboardWidth}>
                        <TabsContent value="schedules" asChild>
                          <div>
                            {selectedEvent ? (
                              <DashboardEventDetails
                                schedule={selectedEvent}
                                isOrgPrivacyEnabled={isOrgPrivacyEnabled}
                                calendarSwitch={
                                  <div className="flex w-full items-center justify-between gap-2">
                                    {calendarSwitch}{" "}
                                    <Button
                                      variant="ghost"
                                      onClick={() => setSelectedEvent(null)}
                                    >
                                      <CloseIcon className="size-4" />
                                      Close Event
                                    </Button>
                                  </div>
                                }
                              />
                            ) : isOrgPrivacyEnabled ? (
                              // If somehow user is on Attendance tab, show SiteInfo
                              <SiteInfo />
                            ) : (
                              <TeamSchedule daysCount={finalDaysCount} />
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="visitors" asChild>
                          <div>
                            <Visitors />
                          </div>
                        </TabsContent>

                        <TabsContent value="news" asChild>
                          <div>
                            <News />
                          </div>
                        </TabsContent>
                        <TabsContent value="siteInfo" asChild>
                          <div>
                            <SiteInfo />
                          </div>
                        </TabsContent>
                      </DashboardContentWrapper>
                    </div>
                  </ScrollArea.Viewport>
                  <Scrollbar className="z-50 mr-v2-layout-card-scrollbar" />
                </ScrollArea.Root>
              </Tabs>
            </ColumnWidthProvider>
          </div>
        </div>
      </div>

      {oooRequest ? (
        <OOORequest
          onClose={() => {
            setOooRequest(null);
            locationData.invalidateQueries();
          }}
          request={oooRequest}
        />
      ) : null}
      {onConfirmDialogCallback ? (
        <ConfirmModal
          message={
            <p className="text-gray-500 text-sm">
              If you confirm the current reservations on this day will be
              cancelled
            </p>
          }
          onConfirm={onConfirmDialogCallback}
          title={
            <span className="font-bold text-2xl text-parent">
              Do you want to cancel?
            </span>
          }
          onOpenChange={(isOpen) => !isOpen && setOnConfirmDialogCallback(null)}
          isOpen={!!onConfirmDialogCallback}
        />
      ) : null}
    </>
  );
}

const Scrollbar = ({ className }: { className?: string }) => {
  return (
    <ScrollArea.Scrollbar
      orientation="vertical"
      className={cn(
        "z-50 flex w-3 bg-gray-50/50 p-[2px] hover:bg-gray-100",
        className
      )}
    >
      <ScrollArea.Thumb className="relative flex-1 rounded-full bg-gray-400 hover:bg-gray-500" />
    </ScrollArea.Scrollbar>
  );
};
